import { useSuspenseQuery } from "@apollo/client";
import {
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Icon,
  InFlightTransfersHoverCard,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Txt,
} from "@repo/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { PiBuildingOffice } from "react-icons/pi";
import { RiBuilding4Line, RiWallet3Line } from "react-icons/ri";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { EntityLink } from "~/components/link/EntityLink/EntityLink";
import { graphql } from "~/gql/client";
import type { AdminOrgListOrgFragment } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/organisations")({
  head: () => opsHead("Organisations"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(
    graphql(`
    query AdminOrgList {
      organisations {
        ...AdminOrgListOrg
      }
    }

    fragment AdminOrgListOrg on Organisation {
      id
      name
      assetCount
      homeCurrency
      squareLogoUrl
      fixedMonthlyFeeContractEndDate
      businessEntities {
        id
      }
      estimatedValue {
        currency
        amount
      }
      monthlyServiceFeeEstimate {
        blendedServiceFeeRateBps
        feeEstimate {
          currency
          amount
        }
      }
      inflightTotal {
        currency
        amount
      }
      inflightTransfers {
        id
        unitAmount {
            amount
        }
        sourceAsset {
            id
            name
            providerName
            currency
            logoUrl
        }
        destinationAsset {
            id
            name
            providerName
            logoUrl
        }
      }
      latestMonthlyStatement {
          id
          licenseFeeExGst {
              currency
              amount
          }
      }
    }
  `),
  );

  return (
    <AdminPageContainer testId="admin-organisations">
      <AdminPageHeader
        title="Organisations"
        subtitle="All organisations in the Primary database"
      />
      <div>
        {(data.organisations ?? []).length === 0 ? (
          <div>No organisations</div>
        ) : (
          <div>
            <DataTable
              data={data.organisations ?? []}
              columns={columns}
              onRowClick={(row) =>
                navigate({
                  to: "/organisations/$organisationId",
                  params: { organisationId: row.original.id },
                })
              }
              columnSortProps={{
                id: "name",
                desc: false,
              }}
              pageSize={100_000}
              className="overflow-hidden border"
              hidePagination
            />
          </div>
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<AdminOrgListOrgFragment>[] = [
  {
    id: "logo",
    cell: ({ row }) => (
      <Icon
        size="8"
        imageUrl={row.original.squareLogoUrl ?? undefined}
        fallbackIcon={<PiBuildingOffice />}
      />
    ),
  },
  {
    id: "name",
    accessorFn: (row) => row.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <Txt variant="name">{row.original.name ?? "-"}</Txt>,
    sortingFn: (a, b) =>
      (a.original.name ?? "").localeCompare(b.original.name ?? ""),
  },
  {
    id: "businessEntities",
    accessorFn: (row) => row.businessEntities?.length ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Entities" />
    ),
    cell: ({ row }) => (
      <div className="flex gap-4 font-semibold">
        <EntityLink to={`/organisations/${row.original.id}/business-entities`}>
          <div className="flex items-center gap-1">
            {row.original.businessEntities?.length ?? 0}{" "}
            <RiBuilding4Line size={16} />
          </div>
        </EntityLink>
        <EntityLink to={`/assets?organisationId=${row.original.id}`}>
          <div className="flex items-center gap-1">
            {row.original.assetCount} <RiWallet3Line size={16} />
          </div>
        </EntityLink>
      </div>
    ),
    sortingFn: (a, b) =>
      (a.original.businessEntities?.length ?? 0) -
      (b.original.businessEntities?.length ?? 0),
  },
  {
    id: "inFlightNet",
    accessorFn: (row) => row.inflightTotal?.amount ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="In Flight" align="right" />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          {row.original?.inflightTransfers &&
          row.original.inflightTransfers.length > 0 ? (
            <InFlightTransfersHoverCard
              inFlightTransfers={
                row.original.inflightTransfers.flatMap((t) => {
                  if (
                    !t.unitAmount ||
                    !t.destinationAsset ||
                    !t.sourceAsset ||
                    !t.sourceAsset.currency
                  ) {
                    return [];
                  }
                  return [
                    {
                      amount: {
                        amount: t.unitAmount.amount,
                        currency: t.sourceAsset.currency,
                      },
                      source: t.sourceAsset.name,
                      sourceProviderName: t.sourceAsset.providerName ?? "",
                      destination: t.destinationAsset.name,
                      destinationProviderName:
                        t.destinationAsset.providerName ?? "",
                      sourceProviderLogo: t.sourceAsset?.logoUrl ?? undefined,
                      destinationProviderLogo:
                        t.destinationAsset?.logoUrl ?? undefined,
                    },
                  ];
                }) ?? []
              }
            >
              <CurrencyAmountUI
                amount={row.original.inflightTotal?.amount ?? 0}
                currency={
                  row.original.inflightTotal?.currency ??
                  row.original.homeCurrency
                }
              />
            </InFlightTransfersHoverCard>
          ) : (
            <CurrencyAmountUI
              amount={row.original.inflightTotal?.amount ?? 0}
              currency={
                row.original.inflightTotal?.currency ??
                row.original.homeCurrency
              }
            />
          )}
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.inflightTotal?.amount ?? new Decimal(0)).comparedTo(
        b.original.inflightTotal?.amount ?? new Decimal(0),
      ),
  },
  {
    id: "licenseFeeRenewalDate",
    accessorFn: (row) => row.fixedMonthlyFeeContractEndDate ?? "",
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Renewal date"
        align="left"
      />
    ),
    cell: ({ row }) => {
      return row.original.fixedMonthlyFeeContractEndDate ? (
        <div className="flex flex-col items-end">
          <DateUI
            {...{
              date: row.original.fixedMonthlyFeeContractEndDate,
              variant: "DATE",
            }}
          />
        </div>
      ) : (
        "-"
      );
    },
    sortingFn: (a, b) =>
      (a.original.fixedMonthlyFeeContractEndDate ?? new Date()).getTime() -
      (b.original.fixedMonthlyFeeContractEndDate ?? new Date()).getTime(),
  },
  {
    id: "licenseFee",
    accessorFn: (row) =>
      row.latestMonthlyStatement?.licenseFeeExGst ?? new Decimal(0),
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="License fee"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return row.original.latestMonthlyStatement?.licenseFeeExGst ? (
        <div className="flex flex-col items-end">
          <CurrencyAmountUI
            {...row.original.latestMonthlyStatement.licenseFeeExGst}
          />
        </div>
      ) : (
        "-"
      );
    },
    sortingFn: (a, b) =>
      (
        a.original.latestMonthlyStatement?.licenseFeeExGst?.amount ??
        new Decimal(0)
      ).comparedTo(
        b.original.latestMonthlyStatement?.licenseFeeExGst?.amount ??
          new Decimal(0),
      ),
  },
  {
    id: "blendedServiceFeeRate",
    accessorFn: (row) =>
      row.monthlyServiceFeeEstimate?.blendedServiceFeeRateBps?.toNumber() ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Blended Service Fee (bps)"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          <Tooltip delayDuration={250}>
            <TooltipTrigger>
              {row.original.monthlyServiceFeeEstimate?.blendedServiceFeeRateBps
                ?.toDP(2)
                .toNumber() ?? 0}
            </TooltipTrigger>
            <TooltipContent>
              Monthly estimate:{" "}
              <CurrencyAmountUI
                amount={
                  row.original.monthlyServiceFeeEstimate?.feeEstimate?.amount ??
                  0
                }
                currency={
                  row.original.monthlyServiceFeeEstimate?.feeEstimate
                    ?.currency ?? "AUD"
                }
                hideCurrency
              />
            </TooltipContent>
          </Tooltip>
        </div>
      );
    },
  },
  {
    id: "totalBalance",
    accessorFn: (row) => row.estimatedValue?.amount ?? 0,
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Total Balance"
        align="right"
      />
    ),
    cell: ({ row }) => {
      return (
        <div className="flex flex-col items-end">
          <CurrencyAmountUI
            amount={row.original.estimatedValue?.amount ?? 0}
            currency={
              row.original.estimatedValue?.currency ?? row.original.homeCurrency
            }
          />
        </div>
      );
    },
    sortingFn: (a, b) =>
      (a.original.estimatedValue?.amount ?? new Decimal(0)).comparedTo(
        b.original.estimatedValue?.amount ?? new Decimal(0),
      ),
  },
];
